<template>
  <div class="d-flex flex-column">
    <b-card-text class="mb-2">
      <h4>{{ title }}</h4>
    </b-card-text>

    <b-form-checkbox
      v-model="enable"
      name="summary-toggle"
      switch
      @input="changeEnable"
    >
      <p class="font-weight-bold">
        {{ $t(`pages.voiceAlertSettings.enable.${enable}`) }}
      </p>
    </b-form-checkbox>

    <b-card-text class="mb-2">
      {{ desc }}
    </b-card-text>

    <!-- Parameters Description Section -->
    <b-card-text v-if="paramOptions.length">
      <h5>{{ $t("parameter") }}</h5>
      <div v-for="option in paramOptions" :key="option.value">
        <div class="d-flex align-items-center mb-1">
          <span style="width: 210px;" class="parameter-label">{{ option.value }}</span>
          <span class="parameter-desc ml-1">: {{ $t(option.label) }}</span>
        </div>
      </div>
    </b-card-text>

    <!-- Text Editor Section -->
    <div class="d-flex flex-row mb-2">
      <div class="flex-item--half mr-2">
        <div class="d-flex flex-column">
          <h5 class="mb-1" v-if="editorTitle">{{ editorTitle }}</h5>
          <div class="d-flex flex-column">
            <div class="d-flex flex-row mb-1">
              <div class="flex-item-input mr-2">
                <b-form-textarea
                  ref="textarea"
                  class="flex-item--full"
                  :value="currentValue"
                  rows="6"
                  no-resize
                  maxlength="150"
                  @input="handleInput"
                ></b-form-textarea>
                <div class="character-count text-right text-muted">
                {{ currentValue.length }}/150
              </div>
              </div>
              <div>
                <b-form-select
                  v-model="locale"
                  :options="locales"
                  :style="{
                    minWidth: '72px',
                    width: 'auto',
                    maxWidth: '120px',
                  }"
                ></b-form-select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Time Settings -->
    <div class="d-flex flex-row align-items-center mb-1" v-if="closeTime !== undefined">
      <b-form-input
        class="input--time mr-1"
        v-model="closeTime"
        type="number"
        @input="debouncedUpdate"
      ></b-form-input>
      <span>
        <strong>{{ $t("pages.voiceAlertSettings.close.unit") }}</strong>
      </span>
      <span class="ml-2">
        {{ $t("pages.voiceAlertSettings.close.title") }}
      </span>
    </div>

    <div class="d-flex flex-row mb-2" v-if="sendAfter !== undefined">
      <div class="flex-item-input">
        <div class="d-flex flex-row">
          <div class="flex-item--half">
            <div class="d-flex flex-row">
              <b-form-input
                class="input--time mr-1"
                v-model="sendAfter"
                type="number"
                @input="debouncedUpdate"
              ></b-form-input>
              <span>{{ timeNotifyDesc }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BCardText,
  BFormCheckbox,
  BFormInput,
  BFormSelect,
  BFormTextarea,
} from 'bootstrap-vue'
import _isEqual from 'lodash/isEqual'
import debounce from 'lodash/debounce'

export default {
  name: 'VoiceAlertEditor',

  components: {
    BCardText,
    BFormCheckbox,
    BFormInput,
    BFormSelect,
    BFormTextarea,
  },

  props: {
    id: {
      type: String,
      default: '',
    },
    value: {
      type: Object,
      default: () => ({
        closeTime: undefined,
        sendAfter: undefined,
        enable: false,
        locales: {
          en: '',
          zh: '',
        },
      }),
    },
    title: {
      type: String,
      required: true,
    },
    desc: {
      type: String,
      required: true,
    },
    timeNotifyDesc: {
      type: String,
      required: false,
    },
    editorTitle: {
      type: String,
      default: undefined,
    },
    paramOptions: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      locale: 'en',
      locales: [
        { value: 'en', text: 'English' },
        { value: 'zh', text: '中文' },
      ],
      sendAfter: undefined,
      closeTime: undefined,
      originValue: false,
      enable: false,
      en: '',
      zh: '',
    }
  },

  computed: {
    currentValue() {
      if (this.locale === 'zh') return this.zh
      return this.en
    },

    mappedReturnValue() {
      let value = {
        enable: this.enable,
        locales: {
          en: this.en.slice(0, 150),
          zh: this.zh.slice(0, 150),
        },
      }

      if (this.sendAfter) value = { ...value, sendAfter: this.sendAfter };
      if (this.closeTime) value = { ...value, closeTime: this.closeTime };

      return value
    },
  },

  watch: {
    value: {
      handler(newValue) {
        if (_isEqual(this.mappedReturnValue, newValue)) return
        this.mapLocalesToData()
      },
      deep: true,
    },

    mappedReturnValue: {
      handler(newValue) {
        if (_isEqual(this.value, newValue)) return
        this.$emit('input', newValue)
      },
      deep: true,
    },
  },

  methods: {
    handleInput(value) {
      const trimmedValue = value.slice(0, 150)
      switch (this.locale) {
        case 'zh':
          this.zh = trimmedValue
          break
        default:
          this.en = trimmedValue
      }
      this.debouncedUpdate()
    },

    updateValue() {
      const payload = { ...this.mappedReturnValue, id: this.id }
      this.$emit('update:textEditor', payload)
    },

    changeEnable() {
      if (this.originValue === this.enable) return

      this.originValue = this.enable
      const payload = {
        enable: this.enable,
        id: this.id,
      }
      this.$emit('update:enable', payload)
    },

    mapLocalesToData() {
      const { enable, locales, sendAfter, closeTime } = this.value
      const { en, zh } = locales

      if (sendAfter !== undefined) this.sendAfter = sendAfter
      if (closeTime !== undefined) this.closeTime = closeTime

      this.enable = enable
      this.originValue = enable
      this.en = en.slice(0, 150)
      this.zh = zh.slice(0, 150)
    },
  },

  mounted() {
    this.mapLocalesToData()
  },
  created() {
    this.debouncedUpdate = debounce(() => {
    this.updateValue()
    }, 3000)
  },
  beforeDestroy() {
  if (this.debouncedUpdate) {
    this.debouncedUpdate.cancel()
  }
},
}
</script>

<style lang="scss" scoped>
.input--time {
  width: auto !important;
  max-width: 80px !important;
}

.flex-item {
  &--half {
    width: 50%;
  }

  &--full {
    width: 100%;
  }

  &-input {
    flex: 1;
  }
}

.parameter-label {
  font-weight: bold;
  color: #666;
}

.parameter-desc {
  color: #333;
}

.character-count {
  margin-top: 5px;
  font-size: 0.875rem;
}
</style>